// ===========================================
// Leaderboard Page
// ===========================================
// Admin page for viewing school-wide student leaderboard

import { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  EmojiEvents as EmojiEventsIcon,
  Whatshot as WhatshotIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { useLeaderboard } from '../../hooks/useGamification';
import { LeaderboardParams } from '../../types/gamification.types';

// ===========================================
// TYPES
// ===========================================

type SortByOption = 'lessons' | 'streak' | 'pieces' | 'level';

interface SortOption {
  value: SortByOption;
  label: string;
}

const sortOptions: SortOption[] = [
  { value: 'lessons', label: 'Lessons Attended' },
  { value: 'streak', label: 'Current Streak' },
  { value: 'pieces', label: 'Pieces Completed' },
  { value: 'level', label: 'Level' },
];

const limitOptions = [10, 25, 50];

// ===========================================
// COMPONENT
// ===========================================

export default function LeaderboardPage() {
  const [sortBy, setSortBy] = useState<SortByOption>('lessons');
  const [limit, setLimit] = useState<number>(10);

  const params: LeaderboardParams = {
    sortBy,
    limit,
  };

  const { data: leaderboard, isLoading, error } = useLeaderboard(params);

  // ===========================================
  // HELPERS
  // ===========================================

  const getMedalIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return '🥇';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return null;
    }
  };

  const getMedalColor = (rank: number) => {
    switch (rank) {
      case 1:
        return '#FFD700'; // Gold
      case 2:
        return '#C0C0C0'; // Silver
      case 3:
        return '#CD7F32'; // Bronze
      default:
        return 'transparent';
    }
  };

  // ===========================================
  // RENDER
  // ===========================================

  return (
    <Box>
      <PageHeader
        title="Leaderboard"
        subtitle="Student rankings and achievements"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Leaderboard' },
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load leaderboard. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortByOption)}
              label="Sort By"
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Limit</InputLabel>
            <Select
              value={limit}
              onChange={(e) => setLimit(e.target.value as number)}
              label="Limit"
            >
              {limitOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Leaderboard Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Level</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Lessons Attended
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Current Streak
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Pieces Completed
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : !leaderboard || leaderboard.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No students found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              leaderboard.map((entry) => {
                const medal = getMedalIcon(entry.rank);
                const medalColor = getMedalColor(entry.rank);
                const levelColor = entry.currentLevel.color || '#4580E4';
                const levelName = entry.currentLevel.name;

                return (
                  <TableRow
                    key={entry.studentId}
                    sx={{
                      bgcolor: entry.rank <= 3 ? `${medalColor}10` : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {/* Rank */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {medal ? (
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: medalColor,
                              fontSize: '1rem',
                            }}
                          >
                            {medal}
                          </Avatar>
                        ) : (
                          <Typography variant="body1" sx={{ minWidth: 32, textAlign: 'center' }}>
                            {entry.rank}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Student Name */}
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: entry.rank <= 3 ? 600 : 400 }}>
                        {entry.studentName}
                      </Typography>
                    </TableCell>

                    {/* Level */}
                    <TableCell>
                      <Chip
                        label={levelName}
                        size="small"
                        sx={{
                          bgcolor: levelColor,
                          color: '#fff',
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>

                    {/* Lessons Attended */}
                    <TableCell align="right">
                      <Typography variant="body2">{entry.totalLessonsAttended}</Typography>
                    </TableCell>

                    {/* Current Streak */}
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {entry.currentStreak > 0 && <WhatshotIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                        <Typography variant="body2">{entry.currentStreak} weeks</Typography>
                      </Box>
                    </TableCell>

                    {/* Pieces Completed */}
                    <TableCell align="right">
                      <Typography variant="body2">{entry.totalPiecesCompleted}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Stats */}
      {leaderboard && leaderboard.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
          <EmojiEventsIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <Typography variant="body2" color="text.secondary">
            Showing top {leaderboard.length} students
          </Typography>
        </Box>
      )}
    </Box>
  );
}
